import { ApolloError } from '@apollo/client'

import { AcceptInviteWithEmailVerificationMutation, OtpVerificationResult } from 'graphql/generated/scheme'

export const INVITE_ERROR_CODES = {
	/** Invite was sent to a different email than the signed-in account's — offer invited-email verification. */
	EMAIL_MISMATCH: '400_User_051',
	/** Comp / private-ticket invite sent to someone else: it only works for them — never offer verification. */
	PRIVATE_TO_ANOTHER_PERSON: '400_User_053',
	NOT_FOUND: '400_User_033',
} as const

/** `extensions.error_code` of the first GraphQL error, whether Apollo surfaced it as graphQLErrors or a networkError body. */
export function graphQLErrorCode(error: unknown): string | undefined {
	if (!(error instanceof ApolloError)) return undefined
	const networkErrors: any[] = (error.networkError as any)?.result?.errors ?? []
	const first = error.graphQLErrors?.[0] ?? networkErrors[0]
	const code = first?.extensions?.error_code
	return typeof code === 'string' ? code : undefined
}

/**
 * Invite id when a post-auth `?to=` destination is an invite page (the global 401 handler sends
 * `/auth/login?to=/accept-invite/<id>`), so the auth pages can keep their invite-specific options.
 */
export function inviteIdFromRedirectPath(to?: string | null): string | null {
	return to?.match(/^\/accept-invite\/([\w-]+)(?:[?#]|$)/)?.[1] ?? null
}

type AcceptWithCodeResult = AcceptInviteWithEmailVerificationMutation['user']['acceptInviteWithEmailVerification']

export type InviteCodeOutcome =
	| { kind: 'accepted'; redirectUrl?: string | null }
	/** Code was right but the invite/ticket is gone; the code is spent, so a retry needs a new one. */
	| { kind: 'unavailable'; errorMessage?: string | null; redirectUrl?: string | null }
	| { kind: 'wrong'; attemptsLeft: number; blockedUntil: Date | null }
	| { kind: 'expired' }

export function interpretAcceptWithCode(result: AcceptWithCodeResult): InviteCodeOutcome {
	switch (result.verificationResult) {
		case OtpVerificationResult.Succeeded:
			return result.success
				? { kind: 'accepted', redirectUrl: result.redirectUrl }
				: { kind: 'unavailable', errorMessage: result.errorMessage, redirectUrl: result.redirectUrl }
		case OtpVerificationResult.Failed:
			return {
				kind: 'wrong',
				attemptsLeft: Math.max(0, result.maxAttempts - result.failedAttempts),
				blockedUntil: parseFutureDate(result.blockedUntil),
			}
		case OtpVerificationResult.Invalid:
		default:
			return { kind: 'expired' }
	}
}

/** A server timestamp as a Date, or null when missing, unparseable, or already past. */
export function parseFutureDate(value: unknown, now: number = Date.now()): Date | null {
	if (!value) return null
	const date = new Date(value as string)
	return Number.isNaN(date.getTime()) || date.getTime() <= now ? null : date
}

export const isPhoneNoAccountError = (message?: string | null): boolean =>
	!!message && /does not match with any account/i.test(message)
